import { z } from 'zod';
import { CommentStatusTagSchema, DocumentSourceSchema, IssueSeveritySchema, IssueStatusSchema, TaskPrioritySchema, TaskStatusSchema, } from '../enums.js';
import { IdSchema, IsoDateTimeSchema, SourcePathsSchema } from './common.js';
/** A trace written before `tddSectionIds` existed carries a single `tddSectionId` instead.
 * Nothing rewrote those documents, so every read of a stored trace — tasks, pending
 * regeneration proposals, generated drafts awaiting review — passes through this rather than
 * silently dropping the old link. */
function upgradeLegacyTrace(raw) {
    if (!raw || typeof raw !== 'object' || 'tddSectionIds' in raw || !('tddSectionId' in raw)) {
        return raw;
    }
    const { tddSectionId, ...rest } = raw;
    return { ...rest, tddSectionIds: typeof tddSectionId === 'string' ? [tddSectionId] : [] };
}
/** Where a generated task came from — the traceability link back to the TDD sections and
 * PRD user story that produced it. See docs/PLAN.md §6. A list of sections rather than one:
 * the TDD is cut by concern (data_model, api_surface, security...), so a single piece of work
 * usually needs several of them, and an agent reading the task via MCP gets only the sections
 * listed here. */
export const TaskTraceSchema = z.preprocess(upgradeLegacyTrace, z.object({
    prdStoryId: IdSchema.nullable().default(null),
    // .describe() makes a distinct schema instance: reusing IdSchema as-is would serialize (for
    // the LLM's responseJsonSchema) as a $ref to the *nullable* prdStoryId above.
    tddSectionIds: z.array(IdSchema.describe('A TDD section id, e.g. "security"')).default([]),
}));
export function emptyTaskTrace() {
    return { prdStoryId: null, tddSectionIds: [] };
}
/** A trace as read straight from Firestore (no schema parse on that path), in either shape. */
export function readTaskTrace(raw) {
    const parsed = TaskTraceSchema.safeParse(raw ?? {});
    return parsed.success ? parsed.data : emptyTaskTrace();
}
/** projects/{id}/tasks/{taskId} */
export const TaskSchema = z.object({
    id: IdSchema,
    title: z.string().min(1).max(300),
    description: z.string().max(5000).default(''),
    acceptanceCriteria: z.array(z.string().min(1).max(500)).default([]),
    priority: TaskPrioritySchema,
    status: TaskStatusSchema.default('backlog'),
    assigneeUid: IdSchema.nullable().default(null),
    dependsOn: z.array(IdSchema).default([]),
    order: z.number().default(0),
    estimateHours: z.number().nonnegative().nullable().default(null),
    actualHours: z.number().nonnegative().default(0),
    trace: TaskTraceSchema.default(emptyTaskTrace),
    sourceVersionId: IdSchema.nullable().default(null),
    /** 'manual' for a task a user added directly, 'llm' for one produced by generation or
     * regeneration. Reuses the same provenance values as a document version's `source` — see
     * DocumentSourceSchema. Purely informational: regeneration matching is keyed off `trace`, not
     * this field. Defaults to 'manual' so a task written before this field existed reads as manual
     * rather than falsely implying LLM traceability. */
    source: DocumentSourceSchema.default('manual'),
    /** Fields the API refuses to let regeneration silently overwrite without confirmation. */
    humanEditedFields: z.array(z.string()).default([]),
    labels: z.array(z.string().max(40)).default([]),
    dueDate: z.string().nullable().default(null),
    createdAt: IsoDateTimeSchema,
    createdByUid: IdSchema,
    updatedAt: IsoDateTimeSchema,
});
export const GeneratedTaskDraftSchema = z.object({
    tempKey: z.string(), // stable within one generation batch, used to express dependsOn before ids exist
    title: z.string().min(1).max(300),
    description: z.string().max(5000).default(''),
    acceptanceCriteria: z.array(z.string().min(1).max(500)).default([]),
    priority: TaskPrioritySchema,
    estimateHours: z.number().nonnegative().nullable().default(null),
    dependsOnTempKeys: z.array(z.string()).default([]),
    trace: TaskTraceSchema,
});
/** What task generation grounds on: the accepted TDD (with the PRD's stories alongside), or the
 * accepted PRD alone — for projects that skip the TDD step. */
export const TASK_GENERATE_SOURCES = ['tdd', 'prd'];
export const TaskGenerateSourceSchema = z.enum(TASK_GENERATE_SOURCES);
export const GenerateTasksInputSchema = z
    .object({
    minCount: z.number().int().positive(),
    maxCount: z.number().int().positive(),
    from: TaskGenerateSourceSchema.default('tdd'),
})
    .refine((v) => v.maxCount >= v.minCount, { message: 'maxCount must be >= minCount' });
/** Where a batch awaiting review came from: generated from the accepted TDD, or imported from a
 * task list the user supplied. Imported tasks are the user's own content (the model only
 * structures it), so they're stored with source 'manual' rather than 'llm'. */
export const TASK_BATCH_ORIGINS = ['generated', 'imported'];
export const TaskBatchOriginSchema = z.enum(TASK_BATCH_ORIGINS);
export const ValidateTasksInputSchema = z.object({
    tasks: z.array(GeneratedTaskDraftSchema).min(1),
    origin: TaskBatchOriginSchema.default('generated'),
});
/** The most text one task import reads — roughly 30 pages. */
export const TASK_IMPORT_MAX_TEXT = 50_000;
/** The most tasks one import may produce, so a huge list is split rather than half-read. */
export const TASK_IMPORT_MAX_TASKS = 100;
/** POST /projects/:id/tasks/import — a task list as pasted text, uploaded files, or both, in
 * any shape (bullets, a spreadsheet export, meeting notes…). */
export const ImportTasksInputSchema = z
    .object({
    text: z.string().trim().max(TASK_IMPORT_MAX_TEXT).default(''),
    storagePaths: SourcePathsSchema.default([]),
})
    .refine((v) => v.text.length > 0 || v.storagePaths.length > 0, {
    message: 'Paste a task list or attach a file',
    path: ['text'],
});
export const CreateTaskInputSchema = z.object({
    title: z.string().min(1).max(300),
    description: z.string().max(5000).optional(),
    acceptanceCriteria: z.array(z.string().min(1).max(500)).optional(),
    priority: TaskPrioritySchema,
    assigneeUid: IdSchema.nullable().optional(),
    dependsOn: z.array(IdSchema).optional(),
    estimateHours: z.number().nonnegative().nullable().optional(),
    labels: z.array(z.string().max(40)).optional(),
});
export const UpdateTaskInputSchema = z.object({
    title: z.string().min(1).max(300).optional(),
    description: z.string().max(5000).optional(),
    acceptanceCriteria: z.array(z.string().min(1).max(500)).optional(),
    priority: TaskPrioritySchema.optional(),
    status: TaskStatusSchema.optional(),
    dependsOn: z.array(IdSchema).optional(),
    estimateHours: z.number().nonnegative().nullable().optional(),
    actualHours: z.number().nonnegative().optional(),
    labels: z.array(z.string().max(40)).optional(),
    dueDate: z.string().nullable().optional(),
});
export const AssignTaskInputSchema = z.object({
    assigneeUid: IdSchema.nullable(),
});
/** projects/{id}/tasks/{taskId}/comments/{id} */
export const CommentSchema = z.object({
    id: IdSchema,
    body: z.string().min(1).max(5000),
    statusTag: CommentStatusTagSchema.nullable().default(null),
    authorUid: IdSchema,
    attachmentIds: z.array(IdSchema).default([]),
    /** Project members @mentioned in `body` — see the "comment_mention" notification category.
     * Filtered server-side to actual project members; a client-supplied uid that isn't one is
     * silently dropped rather than rejected. */
    mentionedUids: z.array(IdSchema).default([]),
    createdAt: IsoDateTimeSchema,
});
export const CreateCommentInputSchema = z.object({
    body: z.string().min(1).max(5000),
    statusTag: CommentStatusTagSchema.nullable().optional(),
    attachmentIds: z.array(IdSchema).optional(),
    mentionedUids: z.array(IdSchema).max(20).optional(),
});
/** projects/{id}/tasks/{taskId}/issues/{id} — blockers, rendered in red, notify the lead. */
export const IssueSchema = z.object({
    id: IdSchema,
    title: z.string().min(1).max(300),
    body: z.string().max(5000).default(''),
    severity: IssueSeveritySchema.default('medium'),
    status: IssueStatusSchema.default('open'),
    raisedByUid: IdSchema,
    createdAt: IsoDateTimeSchema,
    resolvedAt: IsoDateTimeSchema.nullable().default(null),
    resolvedByUid: IdSchema.nullable().default(null),
    resolution: z.string().max(2000).optional(),
});
export const CreateIssueInputSchema = z.object({
    title: z.string().min(1).max(300),
    body: z.string().max(5000).optional(),
    severity: IssueSeveritySchema.default('medium'),
});
export const ResolveIssueInputSchema = z.object({
    resolution: z.string().max(2000).optional(),
});
/** projects/{id}/tasks/{taskId}/attachments/{id} */
export const AttachmentSchema = z.object({
    id: IdSchema,
    storagePath: z.string().min(1),
    kind: z.enum(['screenshot', 'file']),
    mime: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    uploadedByUid: IdSchema,
    createdAt: IsoDateTimeSchema,
});
//# sourceMappingURL=task.js.map